import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NgbCalendar, NgbDate, NgbDatepicker, NgbTimeStruct  } from '@ng-bootstrap/ng-bootstrap';
import { cloneDeep } from 'lodash';
import { OptionDateTimeRangeValue } from 'src/app/_stealth/shared/Enum/lookups';

@Component({
    selector: 'app-date-time-picker',
    templateUrl: './date-time-picker.component.html',
    styleUrls: ['./date-time-picker.component.scss'],
    standalone: false
})
export class DateTimePickerComponent implements OnInit{
  @Input('dateRangeType') dateRangeType: number;
  @Input('allowTime') allowTime: boolean = true;
  @Input('allOptions') allOptions: boolean = true;
  @Input('disableFutureDates') disableFutureDates: boolean = false;
  @Input() startOnPrevDay: boolean = false;

  //CSS Event Emitters
  @Output() isCustomEvent: EventEmitter<boolean> = new EventEmitter();
  @Output() cancelEvent: EventEmitter<boolean> = new EventEmitter();

  //Date Range Event Emitters
  @Output() startDateEvent: EventEmitter<Date> = new EventEmitter();
  @Output() endDateEvent: EventEmitter<Date> = new EventEmitter();
  @Output() applyDateRange: EventEmitter<[number, Date, Date]> = new EventEmitter();
  @Output() dateRangeTypeChange: EventEmitter<number> = new EventEmitter<number>();
  @Output() fromDateChange: EventEmitter<NgbDate> = new EventEmitter<NgbDate>();
  @Output() toDateChange: EventEmitter<NgbDate> = new EventEmitter<NgbDate>();

  @ViewChild('dp', { static: false }) datepicker: NgbDatepicker;
  
  //Form Controls
  public rangeTemplateForm: FormGroup;

  isCustom: boolean = false;

  lastAppliedDateRangeType: number;

  @Input() fromDate: NgbDate;
  @Input() toDate: NgbDate | null = null;
  fullStartDate: Date = new Date();
  fullEndDate: Date = new Date();

  hoveredDate: NgbDate | null = null;
  maxDate: NgbDate;

  // convenience getter for easy access to form fields
  get f() {
    return this.rangeTemplateForm.controls;
  }

  get optionDateRangeValue() {
    if (this.dateRangeType === null || this.dateRangeType === undefined) {
      this.dateRangeType = 0;
    }

    return OptionDateTimeRangeValue[this.dateRangeType];
  }

  constructor(
    private fb: FormBuilder,
    private calendar: NgbCalendar,
    private cdr: ChangeDetectorRef) {
    //Default to todays date in the users TZ
    this.fromDate = calendar.getToday();  
  }

  ngOnInit(): void {
    this.initForm();
    this.rangeValueChanged();

    if (this.disableFutureDates) {
      const yesterday = this.calendar.getPrev(this.calendar.getToday());
      this.maxDate = yesterday;
    }

    if (this.dateRangeType == 6) {
      this.isCustom = true;
      this.isCustomEvent.emit(this.isCustom);
      this.selectDateRange(this.fromDate, this.toDate!);
    }
    this.lastAppliedDateRangeType = this.dateRangeType;
  }

  //begin:: Form Events
  initForm() {
    this.rangeTemplateForm = this.fb.group({
      range: [this.dateRangeType?.toString()],
      startTime: [{ hour: 0, minute: 0 }],
      endTime: [{ hour: 23, minute: 59 }]
    });

    this.setDefaultStartEndTime();
  }

  setDefaultStartEndTime() {
    //set default start time and end time
    this.fullStartDate.setHours(0, 0, 0);
    this.fullEndDate.setHours(23, 59, 0);
  }

  resetDefault() {
    this.f.range.setValue(this.lastAppliedDateRangeType.toString());
  }

  cancel() {
    this.resetDefault();
    this.isCustomEvent.emit(false);
    this.cancelEvent.emit(true);
  }

  selectRangeValue(value: number) {
    this.f.range.setValue(value);
    this.lastAppliedDateRangeType = value;
  }

  rangeValueChanged() {
    this.f.range.valueChanges.subscribe(data => {
      this.dateRangeType = data;
      this.dateRangeTypeChange.emit(this.dateRangeType);
      if (data == 6) {
        this.isCustom = true;
        this.isCustomEvent.emit(this.isCustom);

        // only reset dates when last option is not custom
        if (this.lastAppliedDateRangeType != 6) {
          // reset full start and end date
          this.fullStartDate = new Date();
          this.fullEndDate = new Date();
          this.setDefaultStartEndTime();
        }        
      }
      else {
        this.isCustom = false;
        this.isCustomEvent.emit(this.isCustom);
        this.fromDate = this.calendar.getToday()
        this.toDate = null;
      }
    });
  }


  applyRangeValue() {
    // if custom dateRangeType(6),
    // full start and end date is selected by user
    this.lastAppliedDateRangeType = this.dateRangeType;

    if (this.dateRangeType != 6) {
      this.fullStartDate = new Date();
      this.fullEndDate = new Date();

      if (this.startOnPrevDay) {
        this.fullStartDate.setDate(this.fullStartDate.getDate() - 1);
        this.fullEndDate.setDate(this.fullEndDate.getDate() - 1);
      }
      else {
        this.fullStartDate.setDate(this.fullStartDate.getDate());      
        this.fullEndDate.setDate(this.fullEndDate.getDate());
      }
      
      this.setDefaultStartEndTime();
    }

    if (this.dateRangeType == 1) {
      this.isCustom = false;
      this.isCustomEvent.emit(this.isCustom);

      //get yesterdays date
      this.fullStartDate.setDate(this.fullStartDate.getDate() - 1);
    }
    if (this.dateRangeType == 2) {
      this.isCustom = false;
      this.isCustomEvent.emit(this.isCustom);

      //get the last 7 days
      this.fullStartDate.setDate(this.fullStartDate.getDate() - 7);
    }
    if (this.dateRangeType == 3) {
      this.isCustom = false;
      this.isCustomEvent.emit(this.isCustom);

      //get the last 30 days
      this.fullStartDate.setDate(this.fullStartDate.getDate() - 30);
    }
    if (this.dateRangeType == 4) {
      this.isCustom = false;
      this.isCustomEvent.emit(this.isCustom);

      //get the last 90 days
      this.fullStartDate.setDate(this.fullStartDate.getDate() - 90);
    }
    if (this.dateRangeType == 5) {
      this.isCustom = false;
      this.isCustomEvent.emit(this.isCustom);

      //get the last 7 days
      this.fullStartDate.setDate(this.fullStartDate.getDate() - 365);
    }
    if (this.dateRangeType == 6) {
      this.isCustom = true;
      this.isCustomEvent.emit(this.isCustom);

      let startData = this.f.startTime.value as NgbTimeStruct;
      if (startData != null) {
        this.fullStartDate.setHours(startData.hour ?? 0, startData.minute ?? 0, startData.second ?? 0);
      }

      let endData = this.f.endTime.value as NgbTimeStruct;
      if (endData != null) {
        this.fullEndDate.setHours(endData.hour ?? 23, endData.minute ?? 59, endData.second ?? 59);
      }
    }

    this.startDateEvent.emit(this.getDateUTC(this.fullStartDate));
    this.endDateEvent.emit(this.getDateUTC(this.fullEndDate));
    this.applyDateRange.emit([this.dateRangeType, this.fullStartDate, this.fullEndDate]);
  }

  applyDateWithParam(dateRangeOption: any) {
    this.dateRangeType = dateRangeOption;
    this.applyRangeValue();
  }

  //end:: Form Events

  //begin:: Calendar Events
  onDateSelection(date: NgbDate) {
    if (!this.fromDate && !this.toDate) {
      this.fromDate = date;
      let value = new Date();
      value.setFullYear(this.fromDate.year, this.fromDate.month - 1, this.fromDate.day);

      this.fullStartDate = value;
      this.fullEndDate = cloneDeep(value);

    } else if (this.fromDate && !this.toDate && date.after(this.fromDate)) {
      this.toDate = date;

      let value = new Date();
      value.setFullYear(this.toDate.year, this.toDate.month - 1, this.toDate.day);

      this.fullEndDate = value;
    } else {
      this.toDate = null;
      this.fromDate = date;

      let value = new Date();
      value.setFullYear(this.fromDate.year, this.fromDate.month - 1, this.fromDate.day);

      this.fullStartDate = value;
      this.fullEndDate = cloneDeep(value);
    }
  }

  selectDateRange(fromDate: NgbDate, toDate: NgbDate) {
    this.fromDate = fromDate;
    this.toDate = toDate;
    // you'd only use this function when you have a custom dateRangeType
    this.lastAppliedDateRangeType = 6;

    let startValue = new Date();
    startValue.setFullYear(this.fromDate.year, this.fromDate.month - 1, this.fromDate.day);
    this.fullStartDate = startValue;

    let endValue = new Date();
    endValue.setFullYear(this.toDate.year, this.toDate.month - 1, this.toDate.day);
    this.fullEndDate = endValue;

    this.cdr.detectChanges();

    if (this.datepicker) {
      this.datepicker.navigateTo(this.fromDate);
    }

    this.fromDateChange.emit(this.fromDate);
    this.toDateChange.emit(this.toDate);    
  }

  isHovered(date: NgbDate) {
    return (
      this.fromDate && !this.toDate && this.hoveredDate && date.after(this.fromDate) && date.before(this.hoveredDate)
    );
  }

  isInside(date: NgbDate) {
    return this.toDate && date.after(this.fromDate) && date.before(this.toDate);
  }

  isRange(date: NgbDate) {
    return (
      date.equals(this.fromDate) ||
      (this.toDate && date.equals(this.toDate)) ||
      this.isInside(date) ||
      this.isHovered(date)
    );
  }
  //end:: Calendar Events



  private getDateUTC(date: Date): Date {
    return new Date(date.getUTCFullYear(), date.getUTCMonth(),
      date.getUTCDate(), date.getUTCHours(), date.getUTCMinutes(),
      date.getUTCSeconds());
  }
}
